// pages/TemplatesPage.jsx
//
// Management UI for user-owned Question Templates and Model-Tier Templates.
// Templates are per-user; admins may additionally flip is_shared=True to make
// a template visible to all users.
//
// Layout: two-tab page. Each tab has a left-hand list of templates and a
// right-hand editor panel. Clicking "+ New" on either side drops a blank
// editor into the right panel.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import AppShell from "../components/AppShell";
import ConfirmDialog from "../components/ConfirmDialog";
import Toast from "../components/Toast";
import UserActionsBar from "../components/UserActionsBar";
import "../components/theme.css";

import {
  questionTemplatesList,
  questionTemplateGet,
  questionTemplateCreate,
  questionTemplateUpdate,
  questionTemplateSetItems,
  questionTemplateDelete,
  modelTierTemplatesList,
  modelTierTemplateGet,
  modelTierTemplateCreate,
  modelTierTemplateUpdate,
  modelTierTemplateDelete,
} from "../services/api";


// Canonical model catalog (synced with backend SUPPORTED_MODELS). Also
// lives in NewAnalysisPage - kept in sync manually; a shared module is a
// future cleanup if drift becomes real.
const MODEL_GROUPS = [
  {
    provider: "OpenAI",
    models: [
      "gpt-4o", "gpt-4o-mini", "gpt-4.1-mini", "o4-mini", "gpt-5", "gpt-5-mini",
    ],
  },
  { provider: "Perplexity", models: ["sonar", "sonar-pro", "sonar-reasoning"] },
  {
    provider: "Gemini",
    models: ["gemini-2.5-flash", "gemini-2.5-flash-lite", "gemini-2.5-pro"],
  },
  {
    provider: "Claude (Anthropic)",
    models: ["claude-sonnet-4-6", "claude-opus-4-6", "claude-haiku-4-5"],
  },
  { provider: "Google AI Overview", models: ["searchapi-ai-overview"] },
];


export default function TemplatesPage({ currentUser }) {
  const [tab, setTab] = useState("questions"); // "questions" | "tiers"
  const [toast, setToast] = useState(null);
  const showToast = (message, type = "success") => setToast({ message, type });
  const isAdmin = !!(currentUser && currentUser.is_admin);
  const navigate = useNavigate();

  return (
    <AppShell
      currentUser={currentUser}
      headerSubtitle="Templates"
      headerRight={
        <UserActionsBar
          mode="module"
          currentUser={currentUser}
          onBackToModules={() => navigate("/modules")}
        />
      }
    >
      <div style={styles.page}>
        <div style={styles.header}>
          <h1 style={styles.title}>Templates</h1>
          <div style={styles.subtitle}>
            Reusable question sets and model-tier presets. Templates are private to
            you by default;{" "}
            {isAdmin
              ? "as an admin, you can additionally mark templates as shared so all users see them."
              : "shared templates (created by admins) are also visible here."}
          </div>
        </div>

        <div style={styles.tabBar}>
          <TabButton
            active={tab === "questions"}
            onClick={() => setTab("questions")}
            label="Question Templates"
          />
          <TabButton
            active={tab === "tiers"}
            onClick={() => setTab("tiers")}
            label="Model Tiers"
          />
        </div>

        <div style={styles.body}>
          {tab === "questions" ? (
            <QuestionTemplatesTab isAdmin={isAdmin} currentUser={currentUser} showToast={showToast} />
          ) : (
            <ModelTierTemplatesTab isAdmin={isAdmin} currentUser={currentUser} showToast={showToast} />
          )}
        </div>

        <Toast
          message={toast?.message}
          type={toast?.type}
          onClose={() => setToast(null)}
        />
      </div>
    </AppShell>
  );
}


function TabButton({ active, onClick, label }) {
  return (
    <button onClick={onClick} style={active ? styles.tabActive : styles.tab}>
      {label}
    </button>
  );
}


// -----------------------------------------------------------
// Question Templates tab
// -----------------------------------------------------------

function QuestionTemplatesTab({ isAdmin, currentUser, showToast }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null); // editing state (new or existing)
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await questionTemplatesList();
      setList(Array.isArray(res?.items) ? res.items : []);
    } catch (e) {
      showToast(e?.message || "Failed to load templates", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setSelectedId(null);
    setDraft({
      id: null,
      title: "",
      industry: "",
      description: "",
      is_shared: false,
      items: [],
    });
  };

  const openExisting = async (id) => {
    setSelectedId(id);
    try {
      const t = await questionTemplateGet(id);
      setDraft({
        id: t.id,
        title: t.title || "",
        industry: t.industry || "",
        description: t.description || "",
        is_shared: !!t.is_shared,
        items: (t.items || []).map((i) => i.text),
        owner_user_id: t.owner_user_id,
      });
    } catch (e) {
      showToast(e?.message || "Failed to load template", "error");
    }
  };

  const save = async () => {
    if (!draft) return;
    if (!draft.title.trim() || !draft.industry.trim()) {
      showToast("Title and industry are required", "error");
      return;
    }
    try {
      let saved;
      if (draft.id == null) {
        saved = await questionTemplateCreate({
          title: draft.title.trim(),
          industry: draft.industry.trim(),
          description: draft.description.trim() || null,
          items: draft.items,
          is_shared: !!draft.is_shared && isAdmin,
        });
      } else {
        saved = await questionTemplateUpdate(draft.id, {
          title: draft.title.trim(),
          industry: draft.industry.trim(),
          description: draft.description.trim() || null,
          is_shared: isAdmin ? !!draft.is_shared : undefined,
        });
        await questionTemplateSetItems(draft.id, draft.items);
        saved = await questionTemplateGet(draft.id);
      }
      showToast(draft.id == null ? "Template created" : "Template updated", "success");
      setSelectedId(saved.id);
      await load();
      await openExisting(saved.id);
    } catch (e) {
      showToast(e?.message || "Save failed", "error");
    }
  };

  const onDelete = async () => {
    if (!draft?.id) return;
    setDeleting(true);
    try {
      await questionTemplateDelete(draft.id);
      showToast("Template deleted", "success");
      setSelectedId(null);
      setDraft(null);
      await load();
    } catch (e) {
      showToast(e?.message || "Delete failed", "error");
    } finally {
      setDeleting(false);
      setConfirmDelete(null);
    }
  };

  const canEdit = useMemo(() => {
    if (!draft) return false;
    if (draft.id == null) return true; // new
    return isAdmin || draft.owner_user_id === currentUser?.id;
  }, [draft, isAdmin, currentUser]);

  return (
    <div style={styles.splitPane}>
      <aside style={styles.listPane}>
        <button onClick={openNew} style={styles.primaryBtn}>+ New Question Template</button>
        {loading && <div style={styles.muted}>Loading…</div>}
        {!loading && list.length === 0 && (
          <div style={styles.empty}>No templates yet. Click above to create your first one.</div>
        )}
        {list.map((t) => (
          <ListRow
            key={t.id}
            active={selectedId === t.id}
            onClick={() => openExisting(t.id)}
            title={t.title}
            subtitle={`${t.industry}${t.is_shared ? " · shared" : ""}`}
          />
        ))}
      </aside>

      <section style={styles.editorPane}>
        {!draft ? (
          <div style={styles.placeholder}>
            Select a template on the left, or click <b>+ New Question Template</b> to start.
          </div>
        ) : (
          <QuestionTemplateEditor
            draft={draft}
            setDraft={setDraft}
            isAdmin={isAdmin}
            canEdit={canEdit}
            onSave={save}
            onAskDelete={() => setConfirmDelete(draft)}
          />
        )}
      </section>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete question template?"
        message={confirmDelete ? `"${confirmDelete.title}" will be removed permanently.` : ""}
        confirmText={deleting ? "Deleting…" : "Delete"}
        cancelText="Cancel"
        onConfirm={onDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}


function QuestionTemplateEditor({ draft, setDraft, isAdmin, canEdit, onSave, onAskDelete }) {
  const patch = (changes) => setDraft({ ...draft, ...changes });

  const updateItem = (idx, value) => {
    const next = [...draft.items];
    next[idx] = value;
    patch({ items: next });
  };
  const addItem = () => patch({ items: [...draft.items, ""] });
  const removeItem = (idx) => patch({ items: draft.items.filter((_, i) => i !== idx) });
  const moveItem = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= draft.items.length) return;
    const next = [...draft.items];
    [next[idx], next[j]] = [next[j], next[idx]];
    patch({ items: next });
  };

  return (
    <div style={styles.editorInner}>
      <div style={styles.editorHeader}>
        <input
          style={styles.titleInput}
          value={draft.title}
          onChange={(e) => patch({ title: e.target.value })}
          placeholder="Template title (required)"
          disabled={!canEdit}
        />
        <div style={{ display: "flex", gap: 8 }}>
          {draft.id != null && canEdit && (
            <button onClick={onAskDelete} style={styles.dangerBtn}>Delete</button>
          )}
          <button onClick={onSave} disabled={!canEdit} style={canEdit ? styles.primaryBtn : styles.disabledBtn}>
            {draft.id == null ? "Create" : "Save changes"}
          </button>
        </div>
      </div>

      <Field label="Industry (required)">
        <input
          style={styles.input}
          value={draft.industry}
          onChange={(e) => patch({ industry: e.target.value })}
          placeholder="e.g. Banking, Pharma, Automotive"
          disabled={!canEdit}
        />
      </Field>

      <Field label="Description">
        <textarea
          style={{ ...styles.input, minHeight: 70 }}
          value={draft.description}
          onChange={(e) => patch({ description: e.target.value })}
          placeholder="What's this template for? When should it be used?"
          disabled={!canEdit}
        />
      </Field>

      {isAdmin && (
        <Field label="Sharing">
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={!!draft.is_shared}
              onChange={(e) => patch({ is_shared: e.target.checked })}
              disabled={!canEdit}
            />
            <span style={{ fontSize: 13 }}>
              Shared - visible to all users (admin only)
            </span>
          </label>
        </Field>
      )}

      <Field label={`Questions (${draft.items.length})`}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {draft.items.map((text, i) => (
            <div key={i} style={styles.itemRow}>
              <span style={styles.itemIndex}>{i + 1}.</span>
              <textarea
                style={{ ...styles.input, minHeight: 48, flex: 1 }}
                value={text}
                onChange={(e) => updateItem(i, e.target.value)}
                placeholder="Question text…"
                disabled={!canEdit}
              />
              <div style={styles.itemActions}>
                <button
                  onClick={() => moveItem(i, -1)}
                  disabled={!canEdit || i === 0}
                  style={styles.iconBtn}
                  title="Move up"
                >
                  ↑
                </button>
                <button
                  onClick={() => moveItem(i, 1)}
                  disabled={!canEdit || i === draft.items.length - 1}
                  style={styles.iconBtn}
                  title="Move down"
                >
                  ↓
                </button>
                <button
                  onClick={() => removeItem(i)}
                  disabled={!canEdit}
                  style={styles.iconBtnDanger}
                  title="Remove"
                >
                  ×
                </button>
              </div>
            </div>
          ))}
          <button onClick={addItem} disabled={!canEdit} style={styles.secondaryBtn}>
            + Add question
          </button>
        </div>
      </Field>

      {!canEdit && draft.id != null && (
        <div style={styles.readonlyNote}>
          This template is shared by another user. You can use it, but not edit it.
        </div>
      )}
    </div>
  );
}


// -----------------------------------------------------------
// Model-Tier Templates tab
// -----------------------------------------------------------

function ModelTierTemplatesTab({ isAdmin, currentUser, showToast }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await modelTierTemplatesList();
      setList(Array.isArray(res?.items) ? res.items : []);
    } catch (e) {
      showToast(e?.message || "Failed to load model tiers", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setSelectedId(null);
    setDraft({
      id: null,
      title: "",
      description: "",
      models: [],
      is_shared: false,
    });
  };

  const openExisting = async (id) => {
    setSelectedId(id);
    try {
      const t = await modelTierTemplateGet(id);
      setDraft({
        id: t.id,
        title: t.title || "",
        description: t.description || "",
        models: Array.isArray(t.models) ? t.models : [],
        is_shared: !!t.is_shared,
        owner_user_id: t.owner_user_id,
      });
    } catch (e) {
      showToast(e?.message || "Failed to load tier", "error");
    }
  };

  const save = async () => {
    if (!draft) return;
    if (!draft.title.trim()) {
      showToast("Title is required", "error");
      return;
    }
    if (!draft.models.length) {
      showToast("Pick at least one model", "error");
      return;
    }
    try {
      let saved;
      if (draft.id == null) {
        saved = await modelTierTemplateCreate({
          title: draft.title.trim(),
          description: draft.description.trim() || null,
          models: draft.models,
          is_shared: !!draft.is_shared && isAdmin,
        });
      } else {
        saved = await modelTierTemplateUpdate(draft.id, {
          title: draft.title.trim(),
          description: draft.description.trim() || null,
          models: draft.models,
          is_shared: isAdmin ? !!draft.is_shared : undefined,
        });
      }
      showToast(draft.id == null ? "Tier created" : "Tier updated", "success");
      setSelectedId(saved.id);
      await load();
      await openExisting(saved.id);
    } catch (e) {
      showToast(e?.message || "Save failed", "error");
    }
  };

  const onDelete = async () => {
    if (!draft?.id) return;
    setDeleting(true);
    try {
      await modelTierTemplateDelete(draft.id);
      showToast("Tier deleted", "success");
      setSelectedId(null);
      setDraft(null);
      await load();
    } catch (e) {
      showToast(e?.message || "Delete failed", "error");
    } finally {
      setDeleting(false);
      setConfirmDelete(null);
    }
  };

  const canEdit = useMemo(() => {
    if (!draft) return false;
    if (draft.id == null) return true;
    return isAdmin || draft.owner_user_id === currentUser?.id;
  }, [draft, isAdmin, currentUser]);

  return (
    <div style={styles.splitPane}>
      <aside style={styles.listPane}>
        <button onClick={openNew} style={styles.primaryBtn}>+ New Model Tier</button>
        {loading && <div style={styles.muted}>Loading…</div>}
        {!loading && list.length === 0 && (
          <div style={styles.empty}>
            No tiers yet. Tiers are reusable model-selection presets (e.g. "Standard",
            "Comprehensive", "Budget") used when starting a new analysis.
          </div>
        )}
        {list.map((t) => (
          <ListRow
            key={t.id}
            active={selectedId === t.id}
            onClick={() => openExisting(t.id)}
            title={t.title}
            subtitle={`${t.models?.length || 0} model${(t.models?.length || 0) === 1 ? "" : "s"}${t.is_shared ? " · shared" : ""}`}
          />
        ))}
      </aside>

      <section style={styles.editorPane}>
        {!draft ? (
          <div style={styles.placeholder}>
            Select a tier on the left, or click <b>+ New Model Tier</b> to create one.
          </div>
        ) : (
          <ModelTierEditor
            draft={draft}
            setDraft={setDraft}
            isAdmin={isAdmin}
            canEdit={canEdit}
            onSave={save}
            onAskDelete={() => setConfirmDelete(draft)}
          />
        )}
      </section>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete model tier?"
        message={confirmDelete ? `"${confirmDelete.title}" will be removed permanently.` : ""}
        confirmText={deleting ? "Deleting…" : "Delete"}
        cancelText="Cancel"
        onConfirm={onDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}


function ModelTierEditor({ draft, setDraft, isAdmin, canEdit, onSave, onAskDelete }) {
  const patch = (changes) => setDraft({ ...draft, ...changes });
  const selected = new Set(draft.models || []);

  const toggleModel = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    patch({ models: Array.from(next) });
  };

  return (
    <div style={styles.editorInner}>
      <div style={styles.editorHeader}>
        <input
          style={styles.titleInput}
          value={draft.title}
          onChange={(e) => patch({ title: e.target.value })}
          placeholder="Tier name (e.g. Standard, Comprehensive)"
          disabled={!canEdit}
        />
        <div style={{ display: "flex", gap: 8 }}>
          {draft.id != null && canEdit && (
            <button onClick={onAskDelete} style={styles.dangerBtn}>Delete</button>
          )}
          <button onClick={onSave} disabled={!canEdit} style={canEdit ? styles.primaryBtn : styles.disabledBtn}>
            {draft.id == null ? "Create" : "Save changes"}
          </button>
        </div>
      </div>

      <Field label="Description">
        <textarea
          style={{ ...styles.input, minHeight: 70 }}
          value={draft.description}
          onChange={(e) => patch({ description: e.target.value })}
          placeholder="When should this tier be used?"
          disabled={!canEdit}
        />
      </Field>

      {isAdmin && (
        <Field label="Sharing">
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={!!draft.is_shared}
              onChange={(e) => patch({ is_shared: e.target.checked })}
              disabled={!canEdit}
            />
            <span style={{ fontSize: 13 }}>Shared (admin only)</span>
          </label>
        </Field>
      )}

      <Field label={`Models (${draft.models.length} selected)`}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {MODEL_GROUPS.map((group) => (
            <div key={group.provider}>
              <div style={styles.providerLabel}>{group.provider}</div>
              <div style={styles.modelGrid}>
                {group.models.map((m) => (
                  <label key={m} style={selected.has(m) ? styles.modelPillActive : styles.modelPill}>
                    <input
                      type="checkbox"
                      checked={selected.has(m)}
                      onChange={() => toggleModel(m)}
                      disabled={!canEdit}
                      style={{ marginRight: 6 }}
                    />
                    {m}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Field>

      {!canEdit && draft.id != null && (
        <div style={styles.readonlyNote}>
          This tier is shared by another user. You can use it, but not edit it.
        </div>
      )}
    </div>
  );
}


// -----------------------------------------------------------
// Small reusable pieces
// -----------------------------------------------------------

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={styles.fieldLabel}>{label}</div>
      {children}
    </div>
  );
}


function ListRow({ active, onClick, title, subtitle }) {
  return (
    <button
      onClick={onClick}
      style={active ? styles.listRowActive : styles.listRow}
    >
      <div style={{ fontWeight: 800, fontSize: 14, color: "#111827" }}>{title || "Untitled"}</div>
      {subtitle && <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{subtitle}</div>}
    </button>
  );
}


// -----------------------------------------------------------
// Styles (black / gold accented, matching existing theme)
// -----------------------------------------------------------

const GOLD = "#d4a017";
const BLACK = "#111827";

const styles = {
  page: {
    padding: "28px 32px",
    maxWidth: 1400,
    margin: "0 auto",
    color: "#111827",
  },
  header: { marginBottom: 18 },
  title: { fontSize: 26, fontWeight: 900, margin: 0, color: BLACK },
  subtitle: { marginTop: 6, fontSize: 13, color: "#4b5563", maxWidth: 820 },
  tabBar: {
    display: "flex",
    gap: 4,
    borderBottom: "1px solid #e5e7eb",
    marginBottom: 18,
  },
  tab: {
    background: "transparent",
    border: "none",
    padding: "12px 18px",
    fontSize: 14,
    fontWeight: 700,
    color: "#6b7280",
    cursor: "pointer",
    borderBottom: "2px solid transparent",
  },
  tabActive: {
    background: "transparent",
    border: "none",
    padding: "12px 18px",
    fontSize: 14,
    fontWeight: 900,
    color: BLACK,
    cursor: "pointer",
    borderBottom: `2px solid ${GOLD}`,
  },
  body: { minHeight: 500 },
  splitPane: {
    display: "grid",
    gridTemplateColumns: "minmax(260px, 320px) 1fr",
    gap: 20,
    alignItems: "start",
  },
  listPane: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: 12,
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    background: "#fafafa",
    minHeight: 400,
  },
  editorPane: {
    padding: 20,
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    background: "#ffffff",
    minHeight: 520,
    boxShadow: "0 2px 14px rgba(15,23,42,0.04)",
  },
  placeholder: {
    color: "#6b7280",
    fontSize: 14,
    padding: 24,
    textAlign: "center",
  },
  editorInner: { display: "flex", flexDirection: "column", gap: 4 },
  editorHeader: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    marginBottom: 14,
  },
  titleInput: {
    flex: 1,
    fontSize: 18,
    fontWeight: 800,
    padding: "10px 12px",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    color: BLACK,
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 900,
    color: "#374151",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: 6,
  },
  input: {
    width: "100%",
    padding: "8px 10px",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    fontSize: 13,
    fontFamily: "inherit",
    color: "#111827",
    background: "#fff",
    boxSizing: "border-box",
  },
  itemRow: {
    display: "flex",
    gap: 8,
    alignItems: "flex-start",
  },
  itemIndex: {
    fontSize: 12,
    fontWeight: 700,
    color: "#6b7280",
    minWidth: 24,
    paddingTop: 12,
  },
  itemActions: { display: "flex", flexDirection: "column", gap: 4 },
  iconBtn: {
    width: 28,
    height: 28,
    borderRadius: 6,
    border: "1px solid #e5e7eb",
    background: "#fff",
    cursor: "pointer",
    fontSize: 14,
  },
  iconBtnDanger: {
    width: 28,
    height: 28,
    borderRadius: 6,
    border: "1px solid #fecaca",
    background: "#fff5f5",
    color: "#b91c1c",
    cursor: "pointer",
    fontSize: 18,
    lineHeight: 1,
  },
  primaryBtn: {
    padding: "10px 14px",
    borderRadius: 10,
    border: `1px solid ${GOLD}`,
    background: BLACK,
    color: GOLD,
    fontWeight: 800,
    cursor: "pointer",
    fontSize: 13,
  },
  secondaryBtn: {
    padding: "8px 12px",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: BLACK,
    fontWeight: 700,
    cursor: "pointer",
    fontSize: 12,
    alignSelf: "flex-start",
  },
  disabledBtn: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    background: "#f3f4f6",
    color: "#9ca3af",
    fontWeight: 700,
    cursor: "not-allowed",
    fontSize: 13,
  },
  dangerBtn: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid #fecaca",
    background: "#fff",
    color: "#b91c1c",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: 13,
  },
  listRow: {
    textAlign: "left",
    padding: "10px 12px",
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    cursor: "pointer",
  },
  listRowActive: {
    textAlign: "left",
    padding: "10px 12px",
    background: "#fff",
    border: `1px solid ${GOLD}`,
    borderRadius: 10,
    cursor: "pointer",
    boxShadow: `0 0 0 2px ${GOLD}22`,
  },
  muted: { fontSize: 12, color: "#6b7280", padding: 8 },
  empty: {
    fontSize: 12,
    color: "#6b7280",
    padding: 12,
    textAlign: "center",
    lineHeight: 1.5,
  },
  readonlyNote: {
    marginTop: 14,
    padding: 10,
    background: "#fef3c7",
    border: "1px solid #fde68a",
    borderRadius: 8,
    fontSize: 12,
    color: "#92400e",
  },
  providerLabel: {
    fontSize: 11,
    fontWeight: 900,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: 6,
  },
  modelGrid: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  modelPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 12px",
    borderRadius: 999,
    border: "1px solid #e5e7eb",
    background: "#fff",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    color: "#111827",
  },
  modelPillActive: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 12px",
    borderRadius: 999,
    border: `1px solid ${GOLD}`,
    background: "#fffbe8",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    color: BLACK,
  },
};
